import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { subWeeks, parseJSON, isBefore } from "date-fns";
import AddIcon from "@icons/myicons/plus-circled.svg?react";
import { Investment } from "@bn/api/apiv1";
import {
  getValueByInvestmentAndBookingType,
  toFirstTwoSignificantDecimal,
  toCurrency,
  toPercent,
} from "@helpers";
import { useInvestmentLink } from "@hooks";
import {
  GainLossPercents,
  GainLossTypography,
  InvestmentInfo,
  Note,
} from "@components";
import { InvestmentActionMenu } from "./InvestmentActionMenu";

const QUOTE_PROVIDER_NONE = "none";

const TotalValuesContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  columnGap: theme.spacing(1),
}));

const checkIsShowModal = (investment: Investment) => {
  const { quote, quoteProvider } = investment;
  const showManualQuoteOptionAfter = subWeeks(new Date(), 2);
  if (quoteProvider === QUOTE_PROVIDER_NONE) return true;
  if (
    !quote?.lastDateTime ||
    isBefore(parseJSON(quote.lastDateTime), showManualQuoteOptionAfter)
  )
    return true;
  return false;
};

interface Props {
  investment: Investment;
  image?: string | null;
  noteComment?: string;
  onRowClick?: (investment: Investment) => void;
  depotId?: number;
  onClickAddQuoteModal: (investment: Investment) => void;
  onAddBookingInvestment: (investment: Investment) => void;
  onEditInvestment: (investment: Investment) => void;
  onDeleteInvestment: (investment: Investment) => void;
}

export const MobileInvestmentItem = ({
  investment,
  image,
  noteComment,
  depotId,
  onRowClick,
  onClickAddQuoteModal,
  onAddBookingInvestment,
  onEditInvestment,
  onDeleteInvestment,
}: Props) => {
  const { t } = useTranslation();
  const getInvestmentUrl = useInvestmentLink(depotId);

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        py={2}
        gap={2}
        role="button"
        tabIndex={0}
        onClick={() => onRowClick?.(investment)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onRowClick?.(investment);
          }
        }}
        sx={{ cursor: "pointer" }}
      >
        <Box flex={1} minWidth={0} overflow="hidden">
          <InvestmentInfo
            item={investment}
            image={image}
            href={getInvestmentUrl(investment)}
          />
          <Note comment={noteComment} />
          <Typography variant="body4" color="textSecondary">
            {toFirstTwoSignificantDecimal(investment.numberOfLots)}{" "}
            {t("domains.investments.investmentTable.pieces")}{" "}
            {getValueByInvestmentAndBookingType({
              value: investment.purchasePrice ?? 0,
              investmentType: investment.type,
              currency: investment.currency,
            })}
          </Typography>
          <TotalValuesContainer>
            <GainLossTypography
              variant="body4"
              value={investment.returnOnInvestmentInPercentAC ?? 0}
            >
              {investment.currentMarketValueAC
                ? toCurrency(
                    investment.currentMarketValueAC,
                    investment.currency
                  )
                : ""}{" "}
              (
              {toPercent(
                investment.returnOnInvestmentInPercentAC ?? 0,
                "auto",
                0,
                1
              )}
              )
            </GainLossTypography>
            <Typography variant="body4">
              {t("domains.investments.investmentTable.totalValue")}
            </Typography>
          </TotalValuesContainer>
          {investment.hasDividend && (
            <TotalValuesContainer>
              <GainLossTypography
                variant="body4"
                value={
                  investment.returnOnInvestmentWithDividendInPercentAC ?? 0
                }
              >
                {toCurrency(
                  investment.currentMarketValueWithDividendAC,
                  investment.currency
                )}{" "}
                (
                {toPercent(
                  investment.returnOnInvestmentWithDividendInPercentAC ?? 0,
                  "auto",
                  0,
                  1
                )}
                )
              </GainLossTypography>
              <Typography variant="body4">
                + {t("domains.investments.investmentTable.dividendsValue")}
              </Typography>
            </TotalValuesContainer>
          )}
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          gap={1}
          flexShrink={0}
        >
          <InvestmentActionMenu
            onAddBooking={() => onAddBookingInvestment(investment)}
            onEdit={() => onEditInvestment(investment)}
            onDelete={() => onDeleteInvestment(investment)}
          />
          {Boolean(investment.quote?.changePercentAbs) && (
            <GainLossPercents value={investment.quote?.changePercentAbs ?? 0} />
          )}
          {checkIsShowModal(investment) && (
            <Button
              variant="tertiary"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onClickAddQuoteModal(investment);
              }}
            >
              {t("domains.investments.investmentTable.addQuote.quote")}
            </Button>
          )}
          <Typography variant="body4" noWrap>
            {getValueByInvestmentAndBookingType({
              value: investment.quote?.last ?? 0,
              investmentType: investment.type,
              currency: investment.currency,
              percentageRange: "0-100",
            })}
          </Typography>
        </Box>
      </Box>
      <Divider />
    </>
  );
};
